/**
 * Daily equity reconstruction from orders + activities + historical
 * closing prices.
 *
 * Why this exists
 * ---------------
 * WHB's per-account `states[]` snapshots have two problems:
 *   1. They start late (typically ~2025-09) so the ALL chart range
 *      silently hides the pre-snapshot portion of account history.
 *   2. Per Sergey (2026-04-22): "you should not rely on WHB balances
 *      after Sep 14, as they have errors as well." — so the states
 *      themselves aren't a trustworthy source of truth even where
 *      they exist.
 *
 * The fix: replace `states[]` entirely with a day-by-day simulation
 * driven by the primary-source data the WHB API does provide reliably:
 *   • `/financial-activities` — every cash-impacting event since
 *     inception (deposits, withdrawals, dividends, interest, fees).
 *   • `/trading-history`      — every order (buy/sell + qty + price).
 *   • `/api/market/quotes`    — historical close per ticker per day
 *                               (via our Cloudflare function, Yahoo v8).
 *
 * Algorithm
 * ---------
 * For each calendar day D from first-activity to today:
 *
 *   1. Carry `cash` and `positions[ticker]` from D-1 (zero on day 0).
 *   2. Apply every activity dated D in chronological order:
 *        cash += activity.signedAmount
 *        (CashDeposit, CashWithdrawal, JournalEntryCash, Dividend,
 *         Interest, Fee — the `Cashflow` adapter already converts
 *         withdrawals / fees to negative signed amounts.)
 *   3. Apply every order dated D:
 *        buy:  cash -= order.total;  positions[sym] += qty
 *        sell: cash += order.total;  positions[sym] -= qty
 *      `order.total` is the API's authoritative cash delta
 *      (qty × price for commission-free Alpaca; any fees are separate
 *       `Fee` activities so no double-counting).
 *   4. Mark to market using close[ticker, D] with forward-fill across
 *      weekends / holidays (take the most recent close ≤ D). Days
 *      before a ticker's first quote are valued at zero for that
 *      ticker — defensive against tickers whose IPO post-dates a
 *      backfilled pre-state day.
 *   5. equity[D] = cash + Σ positions[s] × close[s, D]
 *
 * Splits / corporate actions: Yahoo's `adjClose` is split+dividend
 * adjusted, so using `adjClose` directly would misrepresent the
 * day-D holding value. We use raw `close` and treat each position as
 * a share count that the PWA doesn't retroactively adjust — if the
 * user had 10 AAPL before a 4:1 split, they now have 40 per the
 * broker's books (and `/trading-history` will reflect the post-split
 * share count on subsequent trades). If a split crossed our window
 * undocumented, the pre-split day would read ~4× low. Acceptable
 * approximation until/unless we add split-event ingestion.
 *
 * Output
 * ------
 * Returns a dense daily array `{ date, cash, stocksValue, equity }`
 * from the earliest activity through yesterday (or the last date
 * supplied in the prices map, whichever is later). Caller slices to
 * the display window.
 */

import type { Cashflow } from "@/data/mock-data";
import type { Order } from "@/data/mock-orders";

/** Historical close price for one ticker on one day. */
export interface TickerQuote {
  date: string;   // ISO yyyy-mm-dd
  close: number;  // unadjusted close
}

/** One split event for one ticker. `ratio` is shares-after ÷ shares-
 *  before — e.g. a 2-for-1 split is `ratio: 2`, a 1-for-10 reverse
 *  split is `ratio: 0.1`. The engine multiplies position qty by this
 *  ratio on the event's effective date so mark-to-market stays
 *  consistent with the broker's post-split share count. */
export interface TickerSplit {
  date: string;
  ratio: number;
}

/** Map of ticker → ascending daily quote array. Input to the engine. */
export type HistoricalQuotesMap = Readonly<Record<string, readonly TickerQuote[]>>;
/** Map of ticker → ascending split-event array. Optional input — when
 *  omitted the engine assumes no splits occurred (equivalent to the
 *  prior behaviour). */
export type HistoricalSplitsMap = Readonly<Record<string, readonly TickerSplit[]>>;

/** One day's reconstructed state. */
export interface ReconstructedDay {
  date: string;         // yyyy-mm-dd
  cash: number;         // running cash balance
  stocksValue: number;  // Σ qty[s] × close[s, date]
  equity: number;       // cash + stocksValue
}

/**
 * One-shot summary emitted at the end of a reconstruction run so the
 * console diagnostic has something concrete to show. Numbers are
 * computed from the inputs + the last day of the output so they
 * reflect what actually landed on the chart.
 */
export interface ReconstructionStats {
  ordersCount: number;
  cashflowsCount: number;
  uniqueTickers: number;
  tickersWithQuotes: number;
  tickersMissingQuotes: string[];
  splitsApplied: number;
  /** Open positions on the last simulated day. */
  positionsOpen: number;
  /** How many of those open positions were marked from a real
   *  Yahoo close on the last day vs fell back to cost basis. The
   *  delta between these and WHB's authoritative balance is almost
   *  always in the cost-basis bucket. */
  positionsMarketMarked: number;
  positionsCostBasisMarked: number;
  /** Final day's cash / stocks / equity — what the hero reads. */
  finalCash: number;
  finalStocksValue: number;
  finalEquity: number;
  /** How many sell fills exceeded the qty we believed the account held
   *  ("orphan sells" — the matching buy predates our orders feed). */
  orphanSells: number;
  /** Sale proceeds attributable to those un-held shares, i.e. the
   *  phantom cash that would be created by crediting the full sell
   *  total. Non-zero here means the orders feed is incomplete — a
   *  large value is the single most likely reason the reconstructed
   *  equity sits above the broker's own number. */
  orphanSellProceeds: number;
  /** Human-readable window the engine ran over. */
  firstDate: string | null;
  lastDate: string | null;
}

export interface ReconstructionInput {
  /** Sorted asc by `date`. */
  cashflows: readonly Cashflow[];
  /** Any order (the engine filters by symbol+date; order in input doesn't matter). */
  orders: readonly Order[];
  /** Ticker → daily close, ascending dates. Missing tickers contribute 0. */
  prices: HistoricalQuotesMap;
  /** Optional ticker → split events. Applied to position qty on each
   *  event's effective date so mark-to-market stays consistent with
   *  the broker's post-split share count recorded in
   *  `/trading-history`. Missing entries mean "no splits" and the
   *  engine behaves as before. */
  splits?: HistoricalSplitsMap;
  /** Run window: inclusive ISO start + inclusive ISO end. If omitted,
   *  start = min(first cashflow date, first order date),
   *  end = max(today, last order date). */
  startDate?: string;
  endDate?: string;
}

// ─────────────────────────────────────────────────────────────────────
// Daily date helpers (shared with the pre-state reconstructor).
// Kept local to avoid importing from the adapter and causing a cycle.
// ─────────────────────────────────────────────────────────────────────

function dailyDateRange(start: string, endInclusive: string): string[] {
  const s = new Date(`${start}T00:00:00Z`);
  const e = new Date(`${endInclusive}T00:00:00Z`);
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || s > e) return [];
  const out: string[] = [];
  const cur = new Date(s);
  while (cur <= e) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

function todayIso(): string {
  // LOCAL date, not UTC. Sergey 2026-04-30: probed live `curveDates`
  // in US-east timezone showed a phantom "2026-05-01" entry duplicating
  // 2026-04-30's equity, because `toISOString()` returns the UTC
  // calendar day — and at evening US-east time, UTC has already rolled
  // over to the next calendar day. Reconstruction then forward-filled
  // one extra day past the user's actual today, which (a) inflated
  // every range slice by a future day and (b) made edge-dot taps on
  // the chart's right edge land on May 1 instead of Apr 30.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function minDate(a: string | undefined, b: string | undefined): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
}

function maxDate(a: string | undefined, b: string | undefined): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

/**
 * Build a per-ticker "day → most recent close on or before that day"
 * lookup. We pre-compute it once per reconstruction so the daily loop
 * does O(1) lookups instead of O(log n) binary searches. For a 2-year
 * window (~730 days) × 20 tickers that's ~15k entries — trivial.
 */
function buildClosesByDay(
  prices: HistoricalQuotesMap,
  dates: readonly string[]
): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  for (const ticker of Object.keys(prices)) {
    const series = prices[ticker] ?? [];
    if (series.length === 0) {
      out[ticker] = {};
      continue;
    }
    const perDay: Record<string, number> = {};
    let idx = 0;
    let lastClose = 0;
    // Stream through the sorted ticker series once, stamping each
    // target day with the last close seen on or before it. Gaps
    // (weekends/holidays) inherit the previous trading day's close.
    for (const d of dates) {
      while (idx < series.length && series[idx]!.date <= d) {
        lastClose = series[idx]!.close;
        idx++;
      }
      // Before the first quote: lastClose stays 0 → position valued
      // at 0 that day. Shouldn't happen in practice (we only fetch
      // tickers the account traded), but keeps NaNs out of the sum.
      perDay[d] = lastClose;
    }
    out[ticker] = perDay;
  }
  return out;
}

/** Group cashflows by day — one sum per day. */
function groupCashflowsByDay(cfs: readonly Cashflow[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const cf of cfs) {
    if (!cf) continue;
    const d = cf.date.slice(0, 10);
    out[d] = (out[d] ?? 0) + cf.amount;
  }
  return out;
}

/** One trade fill broken out so the simulation can track both cash
 *  delta and cost-basis contribution per position. */
interface DayFill {
  symbol: string;
  side: "buy" | "sell";
  qty: number;
  /** Per-share fill price — used for cost-basis tracking on the
   *  fallback path when a ticker has no historical quote. */
  avgFillPrice: number;
  /** qty × avgFillPrice — the authoritative cash delta from the API. */
  total: number;
}

/**
 * Group orders by day, preserving per-fill detail (symbol, side, qty,
 * avgFillPrice, total). Per-fill granularity lets the simulation
 * track a correct per-ticker cost basis for the mark-to-market
 * fallback; aggregated-per-day totals aren't enough because a single
 * day can buy multiple tickers at very different prices and we need
 * to attribute each one correctly.
 */
function groupOrdersByDay(orders: readonly Order[]): Record<string, DayFill[]> {
  const out: Record<string, DayFill[]> = {};
  for (const o of orders) {
    if (!o || !o.submittedAt) continue;
    const d = o.submittedAt.slice(0, 10);
    (out[d] ??= []).push({
      symbol: o.asset,
      side: o.side,
      qty: o.filledQty,
      avgFillPrice: o.avgFillPrice,
      total: o.totalAmount,
    });
  }
  return out;
}

/**
 * Run the day-by-day simulation. See module docstring for the full
 * algorithm and the simplifying assumptions (splits handled via
 * Yahoo event data; un-priced tickers fall back to running cost
 * basis). Returns the daily trace and an optional `stats` payload
 * suitable for `reconstructEquityCurveWithStats`.
 */
export function reconstructEquityCurve(
  input: ReconstructionInput
): ReconstructedDay[] {
  return reconstructEquityCurveWithStats(input).days;
}

/** Same as {@link reconstructEquityCurve} but also returns a
 *  `stats` payload for the console diagnostic. The stats object is
 *  a frozen snapshot of the run — safe to log directly. */
export function reconstructEquityCurveWithStats(
  input: ReconstructionInput
): { days: ReconstructedDay[]; stats: ReconstructionStats } {
  const { cashflows, orders, prices, splits } = input;

  // Pick the window. min/max over every timestamp we have.
  const firstCf = cashflows[0]?.date;
  const firstOrder = orders
    .reduce<string | undefined>(
      (m, o) =>
        o?.submittedAt ? (minDate(m, o.submittedAt.slice(0, 10)) ?? undefined) : m,
      undefined
    );
  const lastOrder = orders
    .reduce<string | undefined>(
      (m, o) =>
        o?.submittedAt ? (maxDate(m, o.submittedAt.slice(0, 10)) ?? undefined) : m,
      undefined
    );
  const start =
    input.startDate ?? minDate(firstCf, firstOrder) ?? undefined;
  const end =
    input.endDate ?? maxDate(lastOrder, todayIso()) ?? undefined;
  const emptyStats: ReconstructionStats = {
    ordersCount: orders.length,
    cashflowsCount: cashflows.length,
    uniqueTickers: 0,
    tickersWithQuotes: 0,
    tickersMissingQuotes: [],
    splitsApplied: 0,
    positionsOpen: 0,
    positionsMarketMarked: 0,
    positionsCostBasisMarked: 0,
    finalCash: 0,
    finalStocksValue: 0,
    finalEquity: 0,
    orphanSells: 0,
    orphanSellProceeds: 0,
    firstDate: null,
    lastDate: null,
  };
  if (!start || !end) return { days: [], stats: emptyStats };

  const dates = dailyDateRange(start, end);
  if (dates.length === 0) return { days: [], stats: emptyStats };

  const cfByDay = groupCashflowsByDay(cashflows);
  const ordersByDay = groupOrdersByDay(orders);
  const closesByTicker = buildClosesByDay(prices, dates);

  // Splits grouped by day for a cheap O(1) lookup in the main loop.
  // Key shape: `${date}|${ticker}` → ratio.
  const splitByDayTicker: Record<string, { ticker: string; ratio: number }[]> = {};
  let splitsInWindow = 0;
  if (splits) {
    for (const ticker of Object.keys(splits)) {
      for (const ev of splits[ticker] ?? []) {
        if (!ev || !ev.date || !Number.isFinite(ev.ratio) || ev.ratio <= 0) continue;
        // Only count splits that fall inside the reconstruction window —
        // historical splits from before the account opened don't affect
        // any position and aren't worth reporting.
        if (ev.date >= dates[0]! && ev.date <= dates[dates.length - 1]!) {
          splitsInWindow++;
        }
        (splitByDayTicker[ev.date] ??= []).push({ ticker, ratio: ev.ratio });
      }
    }
  }

  const positions: Record<string, number> = {};
  // Running cost basis per ticker — used as a mark-to-market fallback
  // when we don't have a historical quote for that ticker on a given
  // day. Without this, an unpriced position would be valued at $0
  // every day, which means every buy order silently eats cash: cash
  // drops by `total` but stocksValue stays flat. Accounts that trade
  // hundreds of tickers (Default Live: 366) trigger this pathology
  // whenever Yahoo rate-limits or a ticker isn't on Yahoo at all —
  // the reconstructed equity collapses to ~−cumulative-deposits.
  //
  // Weighted-average buys; sells reduce qty but keep avg cost
  // constant (FIFO is more accurate but meaningfully more code; the
  // fallback only kicks in when real quotes are unavailable so an
  // extra decimal of P&L accuracy here is noise).
  const costBasisQty: Record<string, number> = {};
  const costBasisAmt: Record<string, number> = {};

  let cash = 0;
  // Orphan-sell diagnostics — see the sell branch below.
  let orphanSells = 0;
  let orphanSellProceeds = 0;
  const out: ReconstructedDay[] = [];
  // Final-day mark-source counters — updated inside the loop, read by
  // the stats builder after the loop ends.
  let lastDayMarketMarked = 0;
  let lastDayCostBasisMarked = 0;

  for (const d of dates) {
    // 0. Apply any stock splits effective today BEFORE processing
    //    fills + marks. Yahoo's split date is the ex-split date: on
    //    that morning, position qty multiplies by the ratio and close
    //    price is reported post-split. If we don't multiply qty, a
    //    2-for-1 split looks like a 50% position drop on ex-split day
    //    — which is exactly the "sudden drop" pattern the user sees.
    //    Cost basis per share also halves so avg-cost fallback math
    //    stays consistent.
    const todaySplits = splitByDayTicker[d];
    if (todaySplits) {
      for (const { ticker, ratio } of todaySplits) {
        if (positions[ticker] != null) {
          positions[ticker] = positions[ticker]! * ratio;
          if (Math.abs(positions[ticker]!) < 1e-9) delete positions[ticker];
        }
        if (costBasisQty[ticker] != null) {
          costBasisQty[ticker] = costBasisQty[ticker]! * ratio;
          // costBasisAmt stays the same — total $ invested doesn't
          // change across a split, only share count does. avgCost per
          // share naturally adjusts: amt / (qty × ratio) = old/ratio.
        }
      }
    }

    // 1. Cash activities (deposits, withdrawals, dividends, interest, fees).
    const cfDelta = cfByDay[d];
    if (cfDelta) cash += cfDelta;

    // 2. Trade fills — each fill updates cash, qty, and (for buys)
    //    the running cost basis used by the mark-to-market fallback.
    const dayFills = ordersByDay[d];
    if (dayFills) {
      for (const fill of dayFills) {
        const sym = fill.symbol;
        if (fill.side === "buy") {
          cash -= fill.total;
          positions[sym] = (positions[sym] ?? 0) + fill.qty;
          costBasisQty[sym] = (costBasisQty[sym] ?? 0) + fill.qty;
          costBasisAmt[sym] =
            (costBasisAmt[sym] ?? 0) + fill.qty * fill.avgFillPrice;
        } else {
          // sell — credit cash, reduce qty, reduce cost basis
          // proportionally to the qty sold so avgCost per share stays
          // stable (FIFO would be more exact; weighted-avg is fine
          // for the fallback path).
          //
          // "Orphan sells" — fills of shares whose matching buys
          // aren't in our orders feed (e.g. a deleted bot's lifetime
          // trades: WHB drops the bot from /trading-history once the
          // bot is deleted, so the buys disappear but the sells
          // remain; likewise anything bought before the account was
          // connected to WHB).
          //
          // BOTH sides of the trade must be clamped (Sergey
          // 2026-08-03). The qty clamp was already here — without it
          // the recon goes short and negative `stocksValue` corrupts
          // the curve. But cash used to credit the FULL sell total,
          // which mints money out of nothing: we never debited cash
          // for shares we didn't know were bought, so crediting their
          // sale proceeds pushes reconstructed equity above the
          // broker's real number and never comes back down.
          //
          // Measured against this account's Alpaca order history, five
          // sells on page 1 alone (MSFT 49.24 sold vs 25.46 bought,
          // RELX/BDX sold with no buy in the feed, …) accounted for
          // ~$25–37k of phantom cash — matching the ~$25k by which the
          // Jul-31 reconstruction ($132,041) overshot Alpaca (~$107k).
          //
          // Crediting proceeds pro-rata to the qty we actually held
          // keeps cash and stocks consistent: an orphan sell of shares
          // we never modelled is now a no-op instead of free money.
          // Fully-covered sells (the overwhelming majority) are
          // unaffected — `sellQty === fill.qty` makes the ratio 1.
          const heldQty = positions[sym] ?? 0;
          const sellQty = Math.min(heldQty, fill.qty);
          const coveredRatio = fill.qty > 0 ? sellQty / fill.qty : 1;
          if (coveredRatio < 1) {
            orphanSells++;
            orphanSellProceeds += fill.total * (1 - coveredRatio);
          }
          cash += fill.total * coveredRatio;
          positions[sym] = heldQty - sellQty;
          const cbQty = costBasisQty[sym] ?? 0;
          const cbAmt = costBasisAmt[sym] ?? 0;
          if (cbQty > 0) {
            const avg = cbAmt / cbQty;
            const newQty = Math.max(0, cbQty - sellQty);
            costBasisQty[sym] = newQty;
            costBasisAmt[sym] = newQty * avg;
          }
        }
        if (Math.abs(positions[sym] ?? 0) < 1e-9) {
          delete positions[sym];
          delete costBasisQty[sym];
          delete costBasisAmt[sym];
        }
      }
    }

    // 3. Mark to market at close. Missing quote falls back to the
    //    running cost basis so the position doesn't silently zero out.
    let stocksValue = 0;
    // Track per-position mark source for the final day's stats. Reset
    // every iteration; the last loop's value is what we surface.
    lastDayMarketMarked = 0;
    lastDayCostBasisMarked = 0;
    for (const sym of Object.keys(positions)) {
      const qty = positions[sym]!;
      const closeOnDay = closesByTicker[sym]?.[d];
      if (typeof closeOnDay === "number" && closeOnDay > 0) {
        stocksValue += qty * closeOnDay;
        lastDayMarketMarked++;
      } else {
        // Cost-basis mark — keeps cash/stocks balanced on buys even
        // when Yahoo can't price the ticker.
        const cbQty = costBasisQty[sym] ?? 0;
        const cbAmt = costBasisAmt[sym] ?? 0;
        const avgCost = cbQty > 0 ? cbAmt / cbQty : 0;
        stocksValue += qty * avgCost;
        lastDayCostBasisMarked++;
      }
    }

    out.push({
      date: d,
      cash: +cash.toFixed(2),
      stocksValue: +stocksValue.toFixed(2),
      equity: +(cash + stocksValue).toFixed(2),
    });
  }

  // Compute final-state stats. `tickersMissingQuotes` lists only
  // tickers the account actually TRADED (from orders), not every
  // ticker Yahoo failed to resolve — that's the set worth chasing
  // when the chart looks wrong.
  const orderTickers = uniqueTickersFromOrders(orders);
  const quoteSet = new Set(Object.keys(prices));
  const missing = orderTickers.filter((t) => !quoteSet.has(t));
  const last = out[out.length - 1];
  const stats: ReconstructionStats = {
    ordersCount: orders.length,
    cashflowsCount: cashflows.length,
    uniqueTickers: orderTickers.length,
    tickersWithQuotes: orderTickers.length - missing.length,
    tickersMissingQuotes: missing,
    splitsApplied: splitsInWindow,
    positionsOpen: Object.keys(positions).length,
    positionsMarketMarked: lastDayMarketMarked,
    positionsCostBasisMarked: lastDayCostBasisMarked,
    finalCash: last?.cash ?? 0,
    finalStocksValue: last?.stocksValue ?? 0,
    finalEquity: last?.equity ?? 0,
    orphanSells,
    orphanSellProceeds: +orphanSellProceeds.toFixed(2),
    firstDate: dates[0] ?? null,
    lastDate: dates[dates.length - 1] ?? null,
  };
  return { days: out, stats };
}

/** Collect the unique tickers mentioned in a list of orders — used
 *  by the client to decide which historical price series to fetch.
 *  Returned sorted for stable IndexedDB keys. */
export function uniqueTickersFromOrders(orders: readonly Order[]): string[] {
  const seen = new Set<string>();
  for (const o of orders) {
    if (o && typeof o.asset === "string" && o.asset.length > 0) {
      seen.add(o.asset.toUpperCase());
    }
  }
  return Array.from(seen).sort();
}

// ─────────────────────────────────────────────────────────────────────
// Per-bot reconstruction
// ─────────────────────────────────────────────────────────────────────
//
// Same engine, narrower inputs. A bot's "account" is its allocated
// slice — it has exactly one cashflow (the user's allocation at
// inception) and a stream of orders attributed to its `botId`. We
// reconstruct mark-to-market equity day by day and walk the curve for
// peak-to-trough MaxDD + a daily-return Sharpe.
//
// **Why this exists.** The /bots/{id}.states[] series is the broker's
// per-day cash+stocks snapshot, but we've verified single-day jumps
// of -38 %, +127 % etc. that show as V-spikes on WHB's own native
// chart — they're stale-price recalcs, not portfolio moves. Walking
// peak-to-trough on raw states[] reports those glitches as drawdowns.
// dailyProfits[] is realized-only on this dataset, so a bot that
// holds an unrealized -50 % position reports DD ≈ 0. Reconstruction
// from orders + market closes is the only way to recover a true
// mark-to-market curve.
//
// Known deviations from the account-level reconstruction:
//   - **Cashflows** are a single synthetic deposit at `inceptionDate`
//     for the bot's `allocatedAmount`. Mid-life allocation changes
//     are not surfaced by the WHB API today, so they show as
//     paper P&L rather than as cashflow events.
//   - **Bot-attributed dividends/interest/fees** aren't surfaced
//     either; they fold into the parent account's activities and are
//     ignored at the bot level.
// Sergey 2026-04-26.

/** Per-bot reconstruction inputs. Cashflows are synthesized inside
 *  the engine (single allocation at inception) — caller doesn't pass
 *  one in, except for optional mid-stream allocation events. */
export interface BotReconstructionInput {
  /** Bot's allocated capital — used as the synthetic inception
   *  deposit. Must be > 0 to produce a non-trivial curve. */
  allocatedAmount: number;
  /** ISO date (full ISO accepted; engine slices to yyyy-mm-dd). The
   *  curve starts here and the synthetic deposit lands on this day. */
  inceptionDate: string;
  /** Orders attributed to this bot — pre-mapped to `Order[]` so the
   *  reconstruction engine can ingest them directly. */
  placedOrders: readonly Order[];
  /** Ticker → daily close, ascending dates. Same map shape used by
   *  the account-level reconstruction. */
  prices: HistoricalQuotesMap;
  /** Optional ticker → splits — same shape as account-level. */
  splits?: HistoricalSplitsMap;
  /** Optional mid-stream allocation events. Sergey 2026-04-30:
   *  archived bots can have their allocation bumped or reduced over
   *  time (e.g. user pulled $4,320 out of bot 070403cf on 2026-02-18).
   *  Each event is a Cashflow merged with the synthetic inception
   *  deposit before the engine runs, so the equity curve reflects the
   *  true ND(t) — equity drops by withdrawal amount on that day, rises
   *  by deposit amount, and TWR isolates trade returns from these
   *  flows. Order doesn't matter (engine groups by day). */
  additionalCashflows?: readonly Cashflow[];
}

/** Per-bot reconstruction output. */
export interface BotReconstructionOutput {
  /** Daily reconstructed equity, oldest first. */
  days: ReconstructedDay[];
  /** Peak-to-trough max drawdown as a fraction (≤ 0). */
  maxDDFraction: number;
  /** Annualised Sharpe (rf = 0). Undefined when fewer than 30 daily
   *  return samples are available. */
  sharpe?: number;
  /** Last 60 reconstructed equity values, oldest first — drives the
   *  per-bot row sparkline and replaces the broker's snapshot one. */
  sparkline: number[];
  /** How many quoted tickers vs missing — emitted for the diagnostic. */
  tickersWithQuotes: number;
  tickersMissingQuotes: string[];
}

/** Run the bot-level reconstruction. Returns an empty output (zero
 *  curve, zero DD, no sparkline) when the inputs can't produce a
 *  meaningful curve (no allocation, no inception date, no orders). */
export function reconstructBotEquityCurve(
  input: BotReconstructionInput
): BotReconstructionOutput {
  const { allocatedAmount, inceptionDate, placedOrders, prices, splits, additionalCashflows } = input;
  const empty: BotReconstructionOutput = {
    days: [],
    maxDDFraction: 0,
    sparkline: [],
    tickersWithQuotes: 0,
    tickersMissingQuotes: [],
  };
  if (!Number.isFinite(allocatedAmount) || allocatedAmount <= 0) return empty;
  if (typeof inceptionDate !== "string" || inceptionDate.length < 10) return empty;
  const inceptionISO = inceptionDate.slice(0, 10);

  // Synthetic cashflow — the bot's "deposit" event. Sized to the
  // bot's allocated capital so day-0 equity == allocatedAmount and
  // the DD walk sees the same baseline the user thinks of as 100 %.
  // Mid-stream allocation events (Sergey 2026-04-30) are appended
  // after; engine groups all cashflows by day so order is irrelevant.
  const cashflows: Cashflow[] = [
    { date: inceptionISO, amount: allocatedAmount, type: "deposit" },
    ...(additionalCashflows ?? [])
  ];

  const { days, stats } = reconstructEquityCurveWithStats({
    cashflows,
    orders: placedOrders,
    prices,
    splits,
    startDate: inceptionISO,
  });
  if (days.length === 0) return empty;

  // Peak-to-trough on the equity curve. Initial peak is allocated
  // amount (== day-0 equity post-deposit) so a strictly-down series
  // still produces a sensible drawdown.
  let peak = days[0]!.equity > 0 ? days[0]!.equity : allocatedAmount;
  let maxDD = 0;
  const rets: number[] = [];
  let prevEq = days[0]!.equity;
  for (let i = 1; i < days.length; i++) {
    const eq = days[i]!.equity;
    if (eq > peak) peak = eq;
    if (peak > 0) {
      const dd = (eq - peak) / peak;
      if (dd < maxDD) maxDD = dd;
    }
    if (prevEq > 0) {
      rets.push((eq - prevEq) / prevEq);
    }
    prevEq = eq;
  }

  // Sharpe — annualised over ~252 trading days, rf = 0. ≥ 30 samples
  // for the variance to be meaningful.
  let sharpe: number | undefined;
  if (rets.length >= 30) {
    const mean = rets.reduce((s, r) => s + r, 0) / rets.length;
    const variance =
      rets.reduce((s, r) => s + (r - mean) ** 2, 0) / rets.length;
    const std = Math.sqrt(variance);
    if (std > 0) sharpe = (mean * 252) / (std * Math.sqrt(252));
  }

  // 90 calendar days ≈ 3 months — common trading-app default ("3M"
  // chip in TradingView/Robinhood). Long enough to read
  // multi-week trend, short enough to feel current.
  const SPARK_LEN = 90;
  const sparkline = days
    .slice(-SPARK_LEN)
    .map((d) => d.equity)
    .filter((v) => Number.isFinite(v));

  return {
    days,
    maxDDFraction: Number.isFinite(maxDD) ? maxDD : 0,
    sharpe,
    sparkline,
    tickersWithQuotes: stats.tickersWithQuotes,
    tickersMissingQuotes: stats.tickersMissingQuotes,
  };
}
